import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Utensils, ChefHat, Egg, Sandwich, Bookmark, ShoppingCart, Trash2, TrendingUp, Award, Sparkles, Star, Leaf, Zap } from 'lucide-react';
import BuildYourOwnCard from '@/components/BuildYourOwnCard';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/context/AuthContext';
import { useCart } from '@/context/CartContext';
import { toast } from 'sonner';
import { api } from '@/utils/apiClient';
import SEO, { SEO_CONFIG } from '@/components/SEO';

export default function BuildYourOwnPage() {
  const [items, setItems] = useState([]);
  const [savedBuilds, setSavedBuilds] = useState([]);
  const [popularBuilds, setPopularBuilds] = useState([]);
  const [chefRecommendations, setChefRecommendations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all'); // 'all', 'saved', 'popular', 'chef'
  
  const { isAuthenticated, user } = useAuth();
  const { addToCart } = useCart();

  useEffect(() => {
    fetchItems();
    fetchChefRecommendations();
    if (isAuthenticated) {
      fetchSavedBuilds();
    }
    fetchPopularBuilds();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  const fetchItems = async () => {
    try {
      const res = await api.get('/api/build-your-own');
      setItems(res.data);
    } catch (err) {
      console.error('Failed to fetch build your own items:', err);
    }
    setLoading(false);
  };

  const fetchChefRecommendations = async () => {
    try {
      const res = await api.get('/api/chef-recommendations');
      setChefRecommendations(res.data);
    } catch (err) {
      console.error('Failed to fetch chef recommendations:', err);
    }
  };

  const fetchSavedBuilds = async () => {
    try {
      const res = await api.get(`/api/saved-builds?user_id=${user?.id || user?.email}`);
      setSavedBuilds(res.data);
    } catch (err) {
      console.error('Failed to fetch saved builds:', err);
    }
  };

  const fetchPopularBuilds = async () => {
    try {
      const res = await api.get('/api/popular-builds?limit=6');
      setPopularBuilds(res.data);
    } catch (err) {
      console.error('Failed to fetch popular builds:', err);
    }
  };

  const deleteSavedBuild = async (buildId) => {
    try {
      await api.delete(`/api/saved-builds/${buildId}?user_id=${user?.id || user?.email}`);
      setSavedBuilds(prev => prev.filter(b => b.id !== buildId));
      toast.success('Build deleted');
    } catch (err) {
      toast.error('Failed to delete build');
    }
  };

  const reorderSavedBuild = async (build) => {
    // Find the original item
    const originalItem = items.find(i => i.id === build.builder_id);
    if (!originalItem) {
      toast.error('Original item no longer available');
      return;
    }

    // Calculate price
    let total = originalItem.base_price;
    Object.values(build.selections || {}).forEach(sectionItems => {
      sectionItems.forEach(sel => {
        total += (sel.price || 0) * (sel.quantity || 1);
      });
    });

    // Create cart item
    const selectedOptions = [];
    Object.entries(build.selections || {}).forEach(([category, selItems]) => {
      selItems.forEach(item => {
        selectedOptions.push(item.name);
      });
    });

    const customItem = {
      id: `${build.builder_id}-${Date.now()}`,
      name: originalItem.name,
      description: selectedOptions.join(', '),
      price: Math.max(0, total),
      image: originalItem.image,
      category: 'build-your-own',
      is_build_your_own: true,
      builder_id: build.builder_id,
      selections: build.selections,
    };

    addToCart(customItem, null, null, build.special_instructions, null, null, null);
    
    // Track reorder
    try {
      await api.post(`/api/saved-builds/${build.id}/reorder`);
    } catch (err) {}
    
    toast.success(`${build.name} added to cart!`);
  };

  // Group items by type for display
  const burgers = items.filter(i => i.id.includes('burger'));
  const breakfast = items.filter(i => i.id.includes('breakfast') || i.id.includes('omelette') || i.id.includes('eggs'));
  const other = items.filter(i => !i.id.includes('burger') && !i.id.includes('breakfast') && !i.id.includes('omelette') && !i.id.includes('eggs'));

  return (
    <div className="pt-24 pb-16" data-testid="build-your-own-page">
      <SEO {...SEO_CONFIG.buildYourOwn} path="/build-your-own" />
      
      {/* Hero */}
      <section className="py-12 sm:py-16 bg-gradient-to-b from-accent/10 to-transparent">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="inline-flex items-center gap-2 px-4 py-2 bg-accent/20 text-accent rounded-full text-sm font-medium mb-4"
          >
            <ChefHat className="w-4 h-4" />
            Have It Your Way
          </motion.div>
          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="font-heading text-4xl sm:text-5xl md:text-6xl text-foreground mb-4"
          >
            Build Your Own
          </motion.h1>
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-muted-foreground max-w-2xl mx-auto text-base sm:text-lg"
          >
            Create your perfect meal exactly the way you like it. Choose your ingredients, 
            customize every detail, and make it uniquely yours.
          </motion.p>
        </div>
      </section>

      {/* How It Works - Instructions at the top */}
      <section className="py-8 sm:py-12 bg-secondary/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
          <h2 className="font-heading text-2xl sm:text-3xl text-center mb-8">How It Works</h2>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="text-center p-6 bg-card rounded-2xl shadow-sm">
              <div className="w-12 h-12 rounded-full bg-accent/10 flex items-center justify-center mx-auto mb-4">
                <span className="text-accent font-bold text-lg">1</span>
              </div>
              <h3 className="font-medium mb-2">Choose Your Base</h3>
              <p className="text-sm text-muted-foreground">
                Start with your preferred base — bun, bread, or eggs cooked your way.
              </p>
            </div>
            <div className="text-center p-6 bg-card rounded-2xl shadow-sm">
              <div className="w-12 h-12 rounded-full bg-accent/10 flex items-center justify-center mx-auto mb-4">
                <span className="text-accent font-bold text-lg">2</span>
              </div>
              <h3 className="font-medium mb-2">Add Your Favorites</h3>
              <p className="text-sm text-muted-foreground">
                Select from premium proteins, fresh toppings, and delicious sauces.
              </p>
            </div>
            <div className="text-center p-6 bg-card rounded-2xl shadow-sm">
              <div className="w-12 h-12 rounded-full bg-accent/10 flex items-center justify-center mx-auto mb-4">
                <span className="text-accent font-bold text-lg">3</span>
              </div>
              <h3 className="font-medium mb-2">Enjoy Your Creation</h3>
              <p className="text-sm text-muted-foreground">
                We'll prepare it fresh, exactly the way you designed it.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-8 sm:py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
          {/* Tab Navigation */}
          <div className="flex gap-2 mb-8 overflow-x-auto pb-2">
            <button
              onClick={() => setActiveTab('all')}
              className={`px-5 py-2.5 rounded-full font-medium text-sm whitespace-nowrap transition-all ${
                activeTab === 'all'
                  ? 'bg-accent text-white shadow-lg'
                  : 'bg-muted hover:bg-muted/80 text-foreground/70'
              }`}
            >
              <Utensils className="w-4 h-4 inline mr-2" />
              All Items
            </button>
            {isAuthenticated && savedBuilds.length > 0 && (
              <button
                onClick={() => setActiveTab('saved')}
                className={`px-5 py-2.5 rounded-full font-medium text-sm whitespace-nowrap transition-all ${
                  activeTab === 'saved'
                    ? 'bg-accent text-white shadow-lg'
                    : 'bg-muted hover:bg-muted/80 text-foreground/70'
                }`}
              >
                <Bookmark className="w-4 h-4 inline mr-2" />
                My Saved ({savedBuilds.length})
              </button>
            )}
            {popularBuilds.length > 0 && (
              <button
                onClick={() => setActiveTab('popular')}
                className={`px-5 py-2.5 rounded-full font-medium text-sm whitespace-nowrap transition-all ${
                  activeTab === 'popular'
                    ? 'bg-accent text-white shadow-lg'
                    : 'bg-muted hover:bg-muted/80 text-foreground/70'
                }`}
              >
                <TrendingUp className="w-4 h-4 inline mr-2" />
                Popular Builds
              </button>
            )}
            {chefRecommendations.length > 0 && (
              <button
                onClick={() => setActiveTab('chef')}
                className={`px-5 py-2.5 rounded-full font-medium text-sm whitespace-nowrap transition-all ${
                  activeTab === 'chef'
                    ? 'bg-gradient-to-r from-amber-500 to-orange-500 text-white shadow-lg'
                    : 'bg-gradient-to-r from-amber-100 to-orange-100 dark:from-amber-900/30 dark:to-orange-900/30 hover:from-amber-200 hover:to-orange-200 text-amber-700 dark:text-amber-300'
                }`}
                data-testid="chef-recommendations-tab"
              >
                <Star className="w-4 h-4 inline mr-2" />
                Chef's Picks
              </button>
            )}
          </div>

          {/* Chef's Recommendations Banner (Always visible if exists) */}
          {chefRecommendations.length > 0 && activeTab === 'all' && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-8 p-6 rounded-2xl bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-950/30 dark:to-orange-950/30 border border-amber-200 dark:border-amber-800"
            >
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-r from-amber-500 to-orange-500 flex items-center justify-center">
                    <Star className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-heading text-xl text-amber-800 dark:text-amber-200">Chef's Recommendations</h3>
                    <p className="text-sm text-amber-700 dark:text-amber-300">
                      {chefRecommendations.length} special {chefRecommendations.length === 1 ? 'creation' : 'creations'} by our chefs
                    </p>
                  </div>
                </div>
                <Button
                  onClick={() => setActiveTab('chef')}
                  className="rounded-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white"
                  data-testid="view-chef-picks-btn"
                >
                  View Chef's Picks
                </Button>
              </div>
            </motion.div>
          )}

          {/* Chef's Recommendations Tab */}
          {activeTab === 'chef' && chefRecommendations.length > 0 && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="space-y-6"
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-full bg-gradient-to-r from-amber-500 to-orange-500 flex items-center justify-center">
                  <Star className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h2 className="font-heading text-2xl sm:text-3xl">Chef's Recommendations</h2>
                  <p className="text-sm text-muted-foreground">Handcrafted combinations by our expert chefs</p>
                </div>
              </div>

              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {chefRecommendations.map((rec, i) => {
                  const BadgeIcon = rec.badge_type === 'seasonal' ? Leaf : 
                                    rec.badge_type === 'new' ? Zap :
                                    rec.badge_type === 'staff_favorite' ? Sparkles : Star;
                  const badgeLabel = rec.badge_type === 'seasonal' ? 'Seasonal' :
                                     rec.badge_type === 'new' ? 'New' :
                                     rec.badge_type === 'staff_favorite' ? 'Staff Favorite' : "Chef's Pick";
                  const badgeColor = rec.badge_type === 'seasonal' ? 'from-green-500 to-emerald-500' :
                                     rec.badge_type === 'new' ? 'from-purple-500 to-pink-500' :
                                     rec.badge_type === 'staff_favorite' ? 'from-blue-500 to-cyan-500' : 'from-amber-500 to-orange-500';
                  
                  return (
                    <motion.div
                      key={rec.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: i * 0.05 }}
                      className="bg-card rounded-2xl border-2 border-amber-200 dark:border-amber-800 overflow-hidden shadow-lg"
                      data-testid={`chef-rec-${rec.id}`}
                    >
                      <div className="relative h-40">
                        <img
                          src={rec.builder_image || 'https://images.pexels.com/photos/1639557/pexels-photo-1639557.jpeg'}
                          alt={rec.name}
                          className="w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                        <div className="absolute top-3 left-3">
                          <span className={`px-3 py-1.5 text-xs font-medium rounded-full bg-gradient-to-r ${badgeColor} text-white flex items-center gap-1`}>
                            <BadgeIcon className="w-3 h-3" />
                            {badgeLabel}
                          </span>
                        </div>
                        <div className="absolute bottom-3 left-3 right-3">
                          <h3 className="font-heading text-xl text-white mb-1">{rec.name}</h3>
                          <p className="text-xs text-white/80">{rec.builder_name}</p>
                        </div>
                      </div>
                      
                      <div className="p-4">
                        {rec.description && (
                          <p className="text-sm text-muted-foreground mb-3 line-clamp-2">{rec.description}</p>
                        )}
                        
                        <div className="flex flex-wrap gap-1 mb-4">
                          {Object.entries(rec.selections || {}).flatMap(([cat, selItems]) =>
                            selItems.slice(0, 4).map((item, idx) => (
                              <span
                                key={`${cat}-${idx}`}
                                className="px-2 py-0.5 bg-amber-200 dark:bg-amber-800 text-amber-900 dark:text-amber-100 text-xs rounded-full font-medium"
                              >
                                {item.name}
                              </span>
                            ))
                          )}
                          {Object.values(rec.selections || {}).flat().length > 4 && (
                            <span className="px-2 py-0.5 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 text-xs rounded-full font-medium">
                              +{Object.values(rec.selections || {}).flat().length - 4} more
                            </span>
                          )}
                        </div>
                        
                        <div className="flex items-center justify-between">
                          <div>
                            {rec.special_price ? (
                              <div className="flex items-center gap-2">
                                <span className="text-lg font-bold text-accent">${rec.special_price.toFixed(2)}</span>
                                <span className="text-sm text-muted-foreground line-through">${rec.calculated_price?.toFixed(2)}</span>
                              </div>
                            ) : (
                              <span className="text-lg font-bold text-accent">${rec.calculated_price?.toFixed(2)}</span>
                            )}
                            <span className="text-xs text-muted-foreground ml-1"></span>
                          </div>
                          <Button
                            onClick={async () => {
                              const originalItem = items.find(item => item.id === rec.builder_id);
                              if (!originalItem) {
                                toast.error('Item no longer available');
                                return;
                              }
                              const selectedOptions = Object.entries(rec.selections || {}).flatMap(([cat, selItems]) =>
                                selItems.map(item => item.name)
                              );
                              const customItem = {
                                id: `${rec.builder_id}-chef-${Date.now()}`,
                                name: `${rec.name} (Chef's Pick)`,
                                description: selectedOptions.join(', '),
                                price: rec.special_price || rec.calculated_price || originalItem.base_price,
                                image: originalItem.image,
                                category: 'build-your-own',
                                is_build_your_own: true,
                                builder_id: rec.builder_id,
                                selections: rec.selections,
                                is_chef_recommendation: true,
                                chef_rec_id: rec.id
                              };
                              addToCart(customItem);
                              // Track order
                              try {
                                await api.post(`/api/chef-recommendations/${rec.id}/order`);
                              } catch (err) {}
                              toast.success(`${rec.name} added to cart!`);
                            }}
                            className="rounded-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white"
                            data-testid={`add-chef-rec-${rec.id}`}
                          >
                            <ShoppingCart className="w-4 h-4 mr-2" />
                            Add
                          </Button>
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </motion.div>
          )}

          {/* Saved Builds Tab */}
          {activeTab === 'saved' && isAuthenticated && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="space-y-6"
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                  <Bookmark className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <h2 className="font-heading text-2xl sm:text-3xl">My Saved Builds</h2>
                  <p className="text-sm text-muted-foreground">Quick reorder your favorite creations</p>
                </div>
              </div>

              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {savedBuilds.map(build => (
                  <motion.div
                    key={build.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-card rounded-2xl border border-border p-5 shadow-sm"
                    data-testid={`saved-build-${build.id}`}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h3 className="font-medium text-lg">{build.name}</h3>
                        <p className="text-sm text-muted-foreground">{build.builder_name}</p>
                      </div>
                      <span className="px-3 py-1 bg-accent/10 text-accent text-sm font-medium rounded-full">
                        ${build.calculated_price?.toFixed(2)}
                      </span>
                    </div>
                    
                    <div className="flex flex-wrap gap-1 mb-4">
                      {Object.entries(build.selections || {}).flatMap(([cat, selItems]) =>
                        selItems.slice(0, 5).map((item, idx) => (
                          <span
                            key={`${cat}-${idx}`}
                            className="px-2 py-0.5 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-100 text-xs rounded-full font-medium"
                          >
                            {item.name}
                          </span>
                        ))
                      )}
                      {Object.values(build.selections || {}).flat().length > 5 && (
                        <span className="px-2 py-0.5 bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300 text-xs rounded-full font-medium">
                          +{Object.values(build.selections || {}).flat().length - 5} more
                        </span>
                      )}
                    </div>
                    
                    <div className="flex gap-2">
                      <Button
                        onClick={() => reorderSavedBuild(build)}
                        className="flex-1 rounded-full bg-accent hover:bg-accent/90"
                        data-testid={`reorder-${build.id}`}
                      >
                        <ShoppingCart className="w-4 h-4 mr-2" />
                        Reorder
                      </Button>
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => deleteSavedBuild(build.id)}
                        className="rounded-full text-destructive hover:bg-destructive/10"
                        data-testid={`delete-saved-${build.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          )}

          {/* Popular Builds Tab */}
          {activeTab === 'popular' && popularBuilds.length > 0 && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="space-y-6"
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-full bg-accent/10 flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-accent" />
                </div>
                <div>
                  <h2 className="font-heading text-2xl sm:text-3xl">Popular Builds</h2>
                  <p className="text-sm text-muted-foreground">See what other customers love</p>
                </div>
              </div>

              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {popularBuilds.map((build, i) => {
                  const originalItem = items.find(item => item.id === build.builder_id);
                  return (
                    <motion.div
                      key={build.id || i}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: i * 0.05 }}
                      className="bg-card rounded-2xl border border-border overflow-hidden shadow-sm"
                    >
                      <div className="relative h-32">
                        <img
                          src={originalItem?.image || 'https://images.pexels.com/photos/1639557/pexels-photo-1639557.jpeg'}
                          alt={build.builder_name}
                          className="w-full h-full object-cover"
                        />
                        <div className="absolute top-3 left-3">
                          {i < 3 && (
                            <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                              i === 0 ? 'bg-yellow-500 text-white' :
                              i === 1 ? 'bg-gray-400 text-white' :
                              'bg-amber-600 text-white'
                            }`}>
                              <Award className="w-3 h-3 inline mr-1" />
                              #{i + 1}
                            </span>
                          )}
                        </div>
                        <div className="absolute bottom-3 right-3 px-2 py-1 bg-white/90 text-sm font-medium rounded-full">
                          ${build.calculated_price?.toFixed(2)}
                        </div>
                      </div>
                      
                      <div className="p-4">
                        <h3 className="font-medium mb-1">{build.builder_name}</h3>
                        <p className="text-xs text-muted-foreground mb-3">
                          Ordered {build.order_count} times
                        </p>
                        
                        <div className="flex flex-wrap gap-1 mb-4">
                          {Object.entries(build.selections || {}).flatMap(([cat, selItems]) =>
                            selItems.slice(0, 4).map((item, idx) => (
                              <span
                                key={`${cat}-${idx}`}
                                className="px-2 py-0.5 bg-orange-200 dark:bg-orange-800 text-orange-900 dark:text-orange-100 text-xs rounded-full font-medium"
                              >
                                {item.name}
                              </span>
                            ))
                          )}
                        </div>
                        
                        <Button
                          onClick={() => {
                            if (!originalItem) {
                              toast.error('Item no longer available');
                              return;
                            }
                            const selectedOptions = Object.entries(build.selections || {}).flatMap(([cat, selItems]) =>
                              selItems.map(item => item.name)
                            );
                            const customItem = {
                              id: `${build.builder_id}-${Date.now()}`,
                              name: originalItem.name,
                              description: selectedOptions.join(', '),
                              price: build.calculated_price || originalItem.base_price,
                              image: originalItem.image,
                              category: 'build-your-own',
                              is_build_your_own: true,
                              builder_id: build.builder_id,
                              selections: build.selections,
                            };
                            addToCart(customItem);
                            toast.success('Popular build added to cart!');
                          }}
                          className="w-full rounded-full bg-accent hover:bg-accent/90"
                        >
                          <ShoppingCart className="w-4 h-4 mr-2" />
                          Try This Build
                        </Button>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </motion.div>
          )}

          {/* All Items Tab */}
          {activeTab === 'all' && (
            <>
              {loading ? (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
                  {[...Array(4)].map((_, i) => (
                    <div key={i} className="bg-muted rounded-3xl h-96 animate-pulse" />
                  ))}
                </div>
              ) : (
                <div className="space-y-12">
                  {/* Burgers Section */}
              {burgers.length > 0 && (
                <div>
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 rounded-full bg-accent/10 flex items-center justify-center">
                      <Sandwich className="w-5 h-5 text-accent" />
                    </div>
                    <h2 className="font-heading text-2xl sm:text-3xl">Build Your Burger</h2>
                  </div>
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
                    {burgers.map(item => (
                      <BuildYourOwnCard key={item.id} item={item} />
                    ))}
                  </div>
                </div>
              )}

              {/* Breakfast Section */}
              {breakfast.length > 0 && (
                <div>
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 rounded-full bg-accent/10 flex items-center justify-center">
                      <Egg className="w-5 h-5 text-accent" />
                    </div>
                    <h2 className="font-heading text-2xl sm:text-3xl">Build Your Breakfast</h2>
                  </div>
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
                    {breakfast.map(item => (
                      <BuildYourOwnCard key={item.id} item={item} />
                    ))}
                  </div>
                </div>
              )}

              {/* A La Carte / Other */}
              {other.length > 0 && (
                <div>
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 rounded-full bg-accent/10 flex items-center justify-center">
                      <Utensils className="w-5 h-5 text-accent" />
                    </div>
                    <h2 className="font-heading text-2xl sm:text-3xl">A La Carte</h2>
                  </div>
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
                    {other.map(item => (
                      <BuildYourOwnCard key={item.id} item={item} />
                    ))}
                  </div>
                </div>
              )}

              {items.length === 0 && (
                <div className="text-center py-16">
                  <Utensils className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground text-lg">
                    No build-your-own items available at the moment.
                  </p>
                </div>
              )}
            </div>
              )}
            </>
          )}
        </div>
      </section>
    </div>
  );
}
